<script setup>
import { ref, watch } from 'vue';
import { maxLength, minLength, requiredIf } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import FormInput from '../../atoms/form/FormInput.vue';
import { customFieldProps } from '../../../utils/custom-fields';
import CustomFieldWrapper from './CustomFieldWrapper.vue';
import FormMessage from '../../atoms/form/FormMessage.vue';
import { validateIf } from '../../../utils/validators';

const props = defineProps({ ...customFieldProps });

const model = defineModel({
    type: String,
});

const rules = {
    model: {
        required: requiredIf(() => props.required),
        minLength: validateIf(props.minValue !== null, minLength(props.minValue)),
        maxLength: validateIf(props.maxValue !== null, maxLength(props.maxValue)),
    },
};

const v$ = useVuelidate(rules, { model }, { $registerAs: `custom-field-${props.id}` });

const hasReachedMinLengthBefore = ref(false);
const stopWatchingMinLength = watch(() => !v$.value.model.minLength.$invalid, (newValue) => {
    if (newValue) {
        hasReachedMinLengthBefore.value = true;
        stopWatchingMinLength();
    }
});

watch(model, () => {
    // Don't spam the user with error messages while still adding text
    if (hasReachedMinLengthBefore.value) {
        v$.value.$touch();
    }
});
</script>

<template>
    <CustomFieldWrapper
        :id="id"
        :error="v$.$error"
        :label="label"
        :required="required"
    >
        <FormInput
            :id="`custom-field-${id}`"
            v-model.trim="model"
            data-qa="custom field text"
            :name="`custom-field-${id}`"
            :placeholder="placeholder"
            :status="v$.$error ? 'error' : ''"
            @blur="v$.$touch"
        />
        <FormMessage
            v-if="v$.$error"
            type="error"
            data-qa="custom field text error message"
        >
            <template v-if="v$.model.required.$invalid">
                Bitte fülle dieses Feld aus.
            </template>
            <template v-else-if="v$.model.minLength.$invalid">
                Bitte gib mindestens {{ minValue }} Zeichen ein.
            </template>
            <template v-else-if="v$.model.maxLength.$invalid">
                Bitte gib maximal {{ maxValue }} Zeichen ein.
            </template>
        </FormMessage>
    </CustomFieldWrapper>
</template>
